import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import { AutocompleteInput } from 'components/common/AdressMap/AutocompleteInput/AutocompleteInput.component'
import { GRAY_900 } from 'constants/styling/theme'
import { Label } from 'components/common/Label'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIInputField } from 'components/common/MUIInputField'
import { ManualAddressCard } from '../ManualAddressCard'
import { ReactGeocodePlace } from 'react-geocode'
import { Stack } from '@mui/material'
import { usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'

export interface Props {
  /** Is address valid */
  addressIsValid: boolean
  /** Action called when address changes */
  handleMapOnChange: (place: ReactGeocodePlace | null, isInitialLookup: boolean) => void
  /** Selected place */
  selectedPlace: ReactGeocodePlace | null
  /** Function to update selected place */
  setSelectedPlace: (place: ReactGeocodePlace | null) => void
}

/**
 * @component Create or update property component in Address step
 * @example <CreateUpdateProperty />
 */
export const CreateUpdateProperty: React.FC<Props> = ({
  addressIsValid,
  handleMapOnChange,
  selectedPlace,
  setSelectedPlace
}) => {
  const { t } = useTranslation(['order'], { keyPrefix: 'step_address' })

  const {
    propertyName,
    setPropertyName,
    isAddressManually,
    isUpdatePropertyAddressFlow,
    setIsAddressManually,
    addressManual,
    streetNumberManual,
    cityManual,
    zipCodeManual,
    countryManual,
  } = usePurchaseFlowRealEstateProperty()

  const [showError, setShowError] = useState<boolean>(false)
  const [isCopyAddressChecked, setIsCopyAddressChecked] = useState<boolean>(false)

  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)
  }

  useEffect(() => {
    logAnalyticsEvent(isAddressManually ? AnalyticsEvent.MANUAL_ADDRESS_SELECTED : AnalyticsEvent.MANUAL_ADDRESS_UNSELECTED, {})
  }, [isAddressManually])

  useEffect(() => {
    if (isCopyAddressChecked) {
      if (isAddressManually) {
        if (!addressManual && !streetNumberManual && !cityManual && !zipCodeManual && !countryManual) return

        const manualAddress = [
          addressManual,
          streetNumberManual,
          cityManual,
          zipCodeManual,
          countryManual
        ].filter(Boolean).join(', ')

        return setPropertyName(manualAddress)
      }

      setPropertyName(selectedPlace?.formatted_address || '')
    }
  }, [addressManual, cityManual, countryManual, isAddressManually, isCopyAddressChecked, selectedPlace, setPropertyName, streetNumberManual, zipCodeManual])

  return (
    <Stack gap="1.6rem">
      {!isAddressManually &&
        <Stack gap=".6rem">
          <Label text={t('property_address')} />

          <AutocompleteInput
            disabled={isAddressManually}
            handleChange={(newGooglePlace, isInitialLookup) => handleMapOnChange(newGooglePlace, isInitialLookup)}
            onInputChange={() => setSelectedPlace(null)}
            error={(selectedPlace && !addressIsValid && !isAddressManually) || (!selectedPlace && isUpdatePropertyAddressFlow) ? t('address_not_selected') : undefined}
          />

          <Label
            text={
              <Trans t={t} i18nKey="manual_address_hint">
                <span
                  style={{ color: GRAY_900, cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => setIsAddressManually(true)}
                >
                  &nbsp;
                </span>
              </Trans>
            }
          />
        </Stack>
      }

      <Stack gap="1.6rem">
        <MUIInputField
          label={t('property_name')}
          placeholder={t('property_name_placeholder')}
          value={propertyName}
          isError={showError && !propertyName}
          onChange={(e) => {
            setPropertyName(e.target.value)
            if (isCopyAddressChecked) setIsCopyAddressChecked(false)
          }}
          onBlur={handleOnInputBlur}
        />
        <Stack width="100%" alignItems="flex-start" justifyContent="flex-start">
          <MUICheckbox
            label={
              <Label text={t('copy_address_checkbox')} color={GRAY_900} />
            }
            checked={isCopyAddressChecked}
            onChange={(e) => {
              setIsCopyAddressChecked(e.target.checked)
              if (e.target.checked) {
                setPropertyName(selectedPlace?.formatted_address || '')
              }
            }}
          />
        </Stack>
      </Stack>

      {isAddressManually &&
        <ManualAddressCard setSelectedPlace={setSelectedPlace} />
      }

    </Stack>
  )
}
